<template>
  <Header navbar-type="goBack" @clicked="goBack" :screen-title="screenTitle" />
  <div v-if="loading" class="h-100 w-100 mx-auto px-3">
    <Skeleton
      class="my-2"
      type="rect"
      rounded
      v-for="i in 5"
      v-bind:key="i"
      fill
      height="9.25rem"
    />
  </div>
  <section v-else>
    <div
      v-if="!showDetails"
      class="h-100 w-100 mx-auto px-3 d-flex flex-column justify-content-between gap-2"
    >
      <BenefitItem
        v-for="benefit in benefitsList"
        v-bind:key="benefit.id"
        :info="benefit"
        @cta="benefitCTA(benefit)"
      />
    </div>
    <BenefitDetails v-else :benefit="selectedBenefit" />
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import Header from "@/shared-components/consumer/Header.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Skeleton from "@/apps/credit-line/components/Skeleton/Skeleton.vue";
import BenefitItem from "@/apps/credit-line/components/CreditCard/BenefitItem.vue";
import BenefitDetails from "@/apps/credit-line/components/CreditCard/BenefitDetails.vue";
import { MasterCardBenefit } from "@/apps/credit-line/types/responses";
import { Service } from "@/apps/credit-line/services/network.service";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { getSettings } from "@/apps/credit-line/helpers/consumerSettings.helper";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";
import { DEFAULT_SETTING } from "@/shared-models/consumer/ISetting";

declare const Android: AndroidInterface;

const { t } = useI18n();
const router = useRouter();
const CreditLineService = new Service();

const loading = ref(true);
const benefitsList = ref<MasterCardBenefit[]>([]);
const showDetails = ref(false);
const selectedBenefit = ref<MasterCardBenefit>();
const screenTitle = ref(t("PC_BENEFITS_TITLE"));
const benefitsPageUrl = ref<string>("");
const shouldUseBenefitUrl = ref(false);

const initializeVirtualCardSettings = async () => {
  const cardsPageSettings = await getSettings(SettingsCategory.CL_CARDS_PAGE);
  const benefitsPageSettings =
    cardsPageSettings["benefitsPageUrl"] ?? DEFAULT_SETTING;

  benefitsPageUrl.value = benefitsPageSettings?.values?.pageUrl as string;

  shouldUseBenefitUrl.value = benefitsPageSettings?.enabled || false;
};

const fetchBenefits = async (): Promise<MasterCardBenefit[] | null> => {
  try {
    const response = await CreditLineService.getMastercardBenefits({
      deviceTag: Android.getDeviceTag(),
    });
    const now = Date.now();
    return response.data?.benefits.filter((x) => x.endDate * 1000 > now);
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ErrorHandler.handleNetworkErrorAndLog(error as any);
    console.log(error);
    return null;
  }
};

const benefitCTA = (benefit: MasterCardBenefit) => {
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.ClickPCBenefit,
    "PC_Benefits",
    benefit.id
  );
  showDetails.value = true;
  selectedBenefit.value = benefit;
  screenTitle.value = benefit.headerTitle;
};

const showNav = () => {
  if (Android && typeof Android?.showNav === "function") {
    Android.showNav();
  }
};

const goBack = () => {
  if (showDetails.value) {
    showDetails.value = false;
    screenTitle.value = t("PC_BENEFITS_TITLE");
  } else {
    router.back();
  }
};

onMounted(async () => {
  await initializeVirtualCardSettings();
  showNav();
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.PageViewPCBenefits,
    "PC_Benefits"
  );
  window.scrollTo(0, 0);
  benefitsList.value = (await fetchBenefits()) as MasterCardBenefit[];
  loading.value = false;

  if (!benefitsList.value?.length && shouldUseBenefitUrl.value) {
    window.location.href = benefitsPageUrl.value;
  }
  Android.finishedLoading();
});
</script>

<style lang="scss" scoped></style>
